import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { useMap } from "react-map-gl/maplibre";

import { useRefListener } from "@/app/hooks/useRefListener";
import { useDeckGlMap } from "@/features/map/DeckGlMapProvider";
import { filterFeaturesById } from "@/features/map/hooks/events/mappers";
import { isLeftClick } from "@/features/map/hooks/events/selectors";
import type {
  EventCallback,
  EventData,
  EventFeature,
} from "@/features/map/hooks/events/types";
import {
  EventPriority,
  useMapInteractions,
} from "@/features/map/MapInteractionsProvider";
import type {
  MaplibreEventCallback,
  MaplibreEventData,
  MaplibreEventType,
} from "@/features/map/MapInteractionsProvider.types";

enum HandlerState {
  IDLE,
  MOUSE_DOWN,
  DRAG_START,
  DRAG,
  DRAG_END,
  HOVER,
  HOVER_END,
}

const DRAG_THRESHOLD = 1;

// Helper function to extract coordinates from touch or mouse events
const getEventCoordinates = (event: MaplibreEventData) => {
  return {
    lat: event.lngLat.lat,
    lng: event.lngLat.lng,
    x: event.point.x,
    y: event.point.y,
  };
};

const useMapEventHandler = ({
  layerIds,
  onClick,
  onContextMenu,
  onDragStart,
  onDrag,
  onDragEnd,
  onHover,
  onHoverEnd,
  options = {
    enableDeckglHover: false,
    filterByFeatureIds: undefined,
    registerOnMap: false,
  },
  priority,
}: {
  layerIds?: string[];
  onClick?: EventCallback;
  onContextMenu?: EventCallback;
  onDragStart?: EventCallback;
  onDrag?: EventCallback;
  onDragEnd?: EventCallback;
  onHover?: EventCallback;
  onHoverEnd?: EventCallback;
  options?: {
    filterByFeatureIds?: string[];
    registerOnMap?: boolean;
    // This options enables hover events exclusively for deckgl. As a side-effect it disables
    // hover events for maplibre. Thus hover does not work for malibre and deckgl layers at the
    // same time
    enableDeckglHover?: boolean;
  };
  priority?: EventPriority;
}) => {
  // Custom hooks
  const { map } = useMap();
  const { map: deckMap } = useDeckGlMap();
  const { subscribe, unsubscribe, pickFeatures } = useMapInteractions();

  // State
  const [selectedFeatures, setSelectedFeatures] = useState<EventFeature[]>([]);

  // Refs
  const state = useRef<HandlerState>(HandlerState.IDLE);
  const mouseStartPosition = useRef<{
    x: number;
    y: number;
    lat: number;
    lng: number;
  }>({
    lat: 0,
    lng: 0,
    x: 0,
    y: 0,
  });
  const lastDragMouseCoordinates = useRef<{ lat: number; lng: number }>(null);
  const currentDragDisplacement = useRef<{ lat: number; lng: number }>(null);
  const totalDragDisplacement = useRef<{ lat: number; lng: number }>(null);
  const deckGlLastHoveredFeatureId = useRef<string | number | null>(null);

  const registerEvent = ({
    type,
    listener,
    forMap = false,
    stateMachineListener = false,
  }: {
    type: MaplibreEventType;
    listener: MaplibreEventCallback;
    forMap?: boolean;
    stateMachineListener?: boolean;
  }) => {
    if (forMap || !layerIds || options.registerOnMap) {
      subscribe(
        type,
        "map",
        listener,
        stateMachineListener ? EventPriority.HANDLER : priority,
      );
      return;
    }

    layerIds.forEach((id) => {
      subscribe(
        type,
        id,
        listener,
        stateMachineListener ? EventPriority.HANDLER : priority,
      );
    });
  };

  const unregisterEvent = ({
    type,
    listener,
    forMap = false,
  }: {
    type: MaplibreEventType;
    listener: MaplibreEventCallback;
    forMap?: boolean;
  }) => {
    if (forMap || !layerIds || options.registerOnMap) {
      unsubscribe(type, "map", listener);
      return;
    }

    layerIds.forEach((id) => {
      unsubscribe(type, id, listener);
    });
  };

  const safeCallback = (fn?: EventCallback) => {
    if (!fn) return () => {};
    return fn;
  };

  const getFilteredFeatures = useCallback(
    ({
      event,
      features,
    }: {
      event: MaplibreEventData;
      features?: EventFeature[];
    }): EventFeature[] => {
      // Extract features either provided from caller or present on event
      const eventFeatures =
        features ?? (event.features.length > 0 ? event.features : undefined);
      return filterFeaturesById({
        features:
          eventFeatures ||
          pickFeatures({
            options: { layerIds },
            point: event.point,
          }),
        filterIds: options.filterByFeatureIds,
      });
    },
    [layerIds, options.filterByFeatureIds],
  );

  const mapEventData = useCallback(
    ({
      event,
      features,
      displacement,
      totalDisplacement,
    }: {
      event: MaplibreEventData;
      features?: EventFeature[];
      displacement?: { lng: number; lat: number };
      totalDisplacement?: { lng: number; lat: number };
    }): EventData => {
      const filteredFeatures = getFilteredFeatures({
        event,
        features,
      });
      const layerProviders = Array.from(
        new Set(
          filteredFeatures.map((feature) => feature.properties.layerProvider),
        ),
      );
      return {
        coordinates: { lat: event.lngLat.lat, lng: event.lngLat.lng },
        displacement,
        features: filteredFeatures,
        layerProviders,
        originalEvent: event.originalEvent,
        point: { x: event.point.x, y: event.point.y },
        totalDisplacement,
      };
    },
    [getFilteredFeatures],
  );

  const setState = (newState: HandlerState) => {
    state.current = newState;
  };

  const getState = () => {
    return state.current;
  };

  //////////////////////////////////
  // Common handler logic

  // Common logic for down/start events
  const handlePointerDown = (event: MaplibreEventData, isTouch: boolean) => {
    if (!isTouch && !isLeftClick(event.originalEvent)) return;

    const coords = getEventCoordinates(event);
    lastDragMouseCoordinates.current = {
      lat: coords.lat,
      lng: coords.lng,
    };
    const matchedFeatures = getFilteredFeatures({ event });
    if (matchedFeatures?.length === 0) return;
    setSelectedFeatures(matchedFeatures);
    setState(HandlerState.MOUSE_DOWN);
    mouseStartPosition.current = coords;
    return true;
  };

  // Common logic for move events (state machine)
  const handlePointerMoveStateMachine = (
    event: MaplibreEventData,
    isTouch: boolean,
  ) => {
    if (
      options.enableDeckglHover &&
      (getState() === HandlerState.IDLE || getState() === HandlerState.HOVER)
    ) {
      const hoveredFeatureId = getFilteredFeatures({ event })?.[0]?.id ?? null;
      if (
        hoveredFeatureId == null &&
        deckGlLastHoveredFeatureId.current != null
      ) {
        setState(HandlerState.HOVER_END);
      } else if (deckGlLastHoveredFeatureId.current !== hoveredFeatureId) {
        deckGlLastHoveredFeatureId.current = hoveredFeatureId;
        setState(HandlerState.HOVER);
      }
    }

    if (!isTouch && !isLeftClick(event.originalEvent)) return;

    const coords = getEventCoordinates(event);
    if (getState() === HandlerState.MOUSE_DOWN) {
      const dx = Math.abs(mouseStartPosition.current.x - coords.x);
      const dy = Math.abs(mouseStartPosition.current.y - coords.y);
      if (dx > DRAG_THRESHOLD || dy > DRAG_THRESHOLD) {
        setState(HandlerState.DRAG_START);
      }
    } else if (getState() === HandlerState.DRAG_START) {
      setState(HandlerState.DRAG);
    } else if (getState() === HandlerState.DRAG) {
      currentDragDisplacement.current = {
        lat: coords.lat - lastDragMouseCoordinates.current.lat,
        lng: coords.lng - lastDragMouseCoordinates.current.lng,
      };
      totalDragDisplacement.current = {
        lat: coords.lat - mouseStartPosition.current.lat,
        lng: coords.lng - mouseStartPosition.current.lng,
      };
    }
  };

  // Common logic for up/end events (state machine)
  const handlePointerUpStateMachine = (
    event: MaplibreEventData,
    isTouch: boolean,
  ) => {
    if (!isTouch && !isLeftClick(event.originalEvent)) return;

    lastDragMouseCoordinates.current = null;
    if (getState() === HandlerState.DRAG) {
      setState(HandlerState.DRAG_END);
      setSelectedFeatures([]);
    } else {
      setState(HandlerState.IDLE);
    }
  };

  // Common logic for move events (callbacks)
  const handlePointerMove = (event: MaplibreEventData) => {
    if (getState() === HandlerState.DRAG_START) {
      currentDragDisplacement.current = {
        lat: 0,
        lng: 0,
      };
      return safeCallback(onDragStart)(mapEventData({ event }));
    } else if (getState() === HandlerState.DRAG) {
      const coords = getEventCoordinates(event);
      lastDragMouseCoordinates.current = {
        lat: coords.lat,
        lng: coords.lng,
      };

      return safeCallback(onDrag)(
        mapEventData({
          displacement: currentDragDisplacement.current,
          event,
          features: selectedFeatures,
          totalDisplacement: totalDragDisplacement.current,
        }),
      );
    } else if (getState() === HandlerState.HOVER) {
      if (options.enableDeckglHover) {
        setState(HandlerState.IDLE);
      }
      return safeCallback(onHover)(mapEventData({ event }));
    } else if (getState() === HandlerState.HOVER_END) {
      if (options.enableDeckglHover) {
        setState(HandlerState.IDLE);
        deckGlLastHoveredFeatureId.current = null;
      }
      return safeCallback(onHoverEnd)(mapEventData({ event }));
    }
  };

  // Common logic for up/end events (callbacks)
  const handlePointerUp = (event: MaplibreEventData, isTouch: boolean) => {
    if (!isTouch && !isLeftClick(event.originalEvent)) return;
    if (getState() !== HandlerState.DRAG_END) return;
    setState(HandlerState.IDLE);
    return safeCallback(onDragEnd)(
      mapEventData({
        displacement: currentDragDisplacement.current,
        event,
        features: selectedFeatures,
        totalDisplacement: totalDragDisplacement.current,
      }),
    );
  };

  //////////////////////////////////
  // State machine listeners

  const _onMouseDownStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (event) => handlePointerDown(event, false),
      [getFilteredFeatures],
    );

  const _onTouchStartStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (event) => handlePointerDown(event, true),
      [getFilteredFeatures],
    );

  const _onMouseMoveStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (event) => handlePointerMoveStateMachine(event, false),
      [
        selectedFeatures,
        lastDragMouseCoordinates,
        mouseStartPosition,
        getFilteredFeatures,
        mapEventData,
        options.enableDeckglHover,
      ],
    );

  const _onTouchMoveStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (event) => handlePointerMoveStateMachine(event, true),
      [
        selectedFeatures,
        lastDragMouseCoordinates,
        mouseStartPosition,
        mapEventData,
      ],
    );

  const _onMouseUpStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (event) => handlePointerUpStateMachine(event, false),
      [selectedFeatures, mapEventData],
    );

  const _onTouchEndStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (event) => handlePointerUpStateMachine(event, true),
      [selectedFeatures, mapEventData],
    );

  const _onMouseEnterStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (_event) => {
        if (
          getState() !== HandlerState.IDLE &&
          getState() !== HandlerState.DRAG_END &&
          getState() !== HandlerState.HOVER_END
        ) {
          return;
        }
        setState(HandlerState.HOVER);
      },
      [mapEventData],
    );

  const _onMouseLeaveStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener(
      (_event) => {
        if (getState() !== HandlerState.HOVER) return;
        setState(HandlerState.HOVER_END);
      },
      [mapEventData],
    );

  const _onDeckGlMouseOutStateMachine: RefObject<MaplibreEventCallback> =
    useRefListener((_event) => {
      if (getState() === HandlerState.HOVER) {
        setState(HandlerState.HOVER_END);
      }
    }, []);

  //////////////////////////////////
  // Callback listeners

  const _onMouseMove: RefObject<MaplibreEventCallback> = useRefListener(
    handlePointerMove,
    [
      selectedFeatures,
      mapEventData,
      onHover,
      onDrag,
      onDragStart,
      currentDragDisplacement,
      totalDragDisplacement,
      options.enableDeckglHover,
    ],
  );

  const _onTouchMove: RefObject<MaplibreEventCallback> = useRefListener(
    handlePointerMove,
    [
      selectedFeatures,
      mapEventData,
      onDrag,
      onDragStart,
      currentDragDisplacement,
      totalDragDisplacement,
    ],
  );

  const _onMouseUp: RefObject<MaplibreEventCallback> = useRefListener(
    (event) => handlePointerUp(event, false),
    [
      selectedFeatures,
      mapEventData,
      onDragEnd,
      totalDragDisplacement,
      currentDragDisplacement,
    ],
  );

  const _onTouchEnd: RefObject<MaplibreEventCallback> = useRefListener(
    (event) => handlePointerUp(event, true),
    [
      selectedFeatures,
      mapEventData,
      onDragEnd,
      totalDragDisplacement,
      currentDragDisplacement,
    ],
  );

  const _onMouseEnter: RefObject<MaplibreEventCallback> = useRefListener(
    (event) => {
      if (getState() !== HandlerState.HOVER) return;
      return safeCallback(onHover)(mapEventData({ event }));
    },
    [mapEventData, onHover],
  );

  const _onMouseLeave: RefObject<MaplibreEventCallback> = useRefListener(
    (event) => {
      if (getState() !== HandlerState.HOVER_END) return;
      return safeCallback(onHoverEnd)(mapEventData({ event }));
    },
    [mapEventData, onHoverEnd],
  );

  const _onClick: MaplibreEventCallback = useCallback(
    (event) => {
      const eventData = mapEventData({ event });
      setSelectedFeatures(eventData.features);
      return safeCallback(onClick)(eventData);
    },
    [mapEventData, onClick],
  );

  const _onContextMenu: MaplibreEventCallback = useCallback(
    (event) => {
      const eventData = mapEventData({ event });
      setSelectedFeatures(eventData.features);
      return safeCallback(onContextMenu)(eventData);
    },
    [mapEventData, onContextMenu],
  );

  //////////////////////////////////
  // Setup listeners

  useEffect(() => {
    if (!map || !deckMap) return;

    const onMouseEnter: MaplibreEventCallback = (event) =>
      _onMouseEnter.current(event);
    const onMouseLeave: MaplibreEventCallback = (event) =>
      _onMouseLeave.current(event);
    const onMouseMove: MaplibreEventCallback = (event) =>
      _onMouseMove.current(event);
    const onMouseUp: MaplibreEventCallback = (event) =>
      _onMouseUp.current(event);

    const onTouchMove: MaplibreEventCallback = (event) =>
      _onTouchMove.current(event);
    const onTouchEnd: MaplibreEventCallback = (event) =>
      _onTouchEnd.current(event);

    const onMouseDownStateMachine: MaplibreEventCallback = (event) =>
      _onMouseDownStateMachine.current(event);
    const onMouseMoveStateMachine: MaplibreEventCallback = (event) =>
      _onMouseMoveStateMachine.current(event);
    const onMouseUpStateMachine: MaplibreEventCallback = (event) =>
      _onMouseUpStateMachine.current(event);
    const onMouseEnterStateMachine: MaplibreEventCallback = (event) =>
      _onMouseEnterStateMachine.current(event);
    const onMouseLeaveStateMachine: MaplibreEventCallback = (event) =>
      _onMouseLeaveStateMachine.current(event);
    const onDeckGlMouseOutStateMachine: MaplibreEventCallback = (event) =>
      _onDeckGlMouseOutStateMachine.current(event);

    const onTouchStartStateMachine: MaplibreEventCallback = (event) =>
      _onTouchStartStateMachine.current(event);
    const onTouchMoveStateMachine: MaplibreEventCallback = (event) =>
      _onTouchMoveStateMachine.current(event);
    const onTouchEndStateMachine: MaplibreEventCallback = (event) =>
      _onTouchEndStateMachine.current(event);

    const shouldHandleDragEvents = !!onDragStart || !!onDrag || !!onDragEnd;
    const shouldHandleHoverEvents = !!onHover || !!onHoverEnd;

    onClick && registerEvent({ listener: _onClick, type: "click" });
    onContextMenu &&
      registerEvent({ listener: _onContextMenu, type: "contextmenu" });

    if (shouldHandleDragEvents) {
      // Mouse events
      registerEvent({
        listener: onMouseDownStateMachine,
        stateMachineListener: true,
        type: "mousedown",
      });
      registerEvent({
        forMap: true,
        listener: onMouseUpStateMachine,
        stateMachineListener: true,
        type: "mouseup",
      });
      registerEvent({
        forMap: true,
        listener: onMouseUp,
        type: "mouseup",
      });

      // Touch events
      registerEvent({
        listener: onTouchStartStateMachine,
        stateMachineListener: true,
        type: "touchstart",
      });
      registerEvent({
        forMap: true,
        listener: onTouchMoveStateMachine,
        stateMachineListener: true,
        type: "touchmove",
      });
      registerEvent({
        forMap: true,
        listener: onTouchMove,
        type: "touchmove",
      });
      registerEvent({
        forMap: true,
        listener: onTouchEndStateMachine,
        stateMachineListener: true,
        type: "touchend",
      });
      registerEvent({
        forMap: true,
        listener: onTouchEnd,
        type: "touchend",
      });
    }

    if (shouldHandleHoverEvents) {
      // TODO ALBA: right now maplibre + deckgl hover cannot coexist
      if (options.enableDeckglHover) {
        registerEvent({
          forMap: true,
          listener: onDeckGlMouseOutStateMachine,
          stateMachineListener: true,
          type: "mouseout",
        });
      } else {
        registerEvent({
          listener: onMouseEnter,
          type: "mouseenter",
        });
        registerEvent({
          listener: onMouseEnterStateMachine,
          stateMachineListener: true,
          type: "mouseenter",
        });
        registerEvent({
          listener: onMouseLeave,
          type: "mouseleave",
        });
        registerEvent({
          listener: onMouseLeaveStateMachine,
          stateMachineListener: true,
          type: "mouseleave",
        });
      }
    }

    if (shouldHandleDragEvents || shouldHandleHoverEvents) {
      registerEvent({
        forMap: true,
        listener: onMouseMoveStateMachine,
        stateMachineListener: true,
        type: "mousemove",
      });
      registerEvent({
        forMap: true,
        listener: onMouseMove,
        type: "mousemove",
      });
    }

    return () => {
      unregisterEvent({ listener: _onClick, type: "click" });
      unregisterEvent({ listener: _onContextMenu, type: "contextmenu" });

      // Mouse events
      unregisterEvent({
        listener: onMouseDownStateMachine,
        type: "mousedown",
      });
      unregisterEvent({
        forMap: true,
        listener: onMouseMove,
        type: "mousemove",
      });
      unregisterEvent({
        forMap: true,
        listener: onMouseMoveStateMachine,
        type: "mousemove",
      });
      unregisterEvent({
        forMap: true,
        listener: onMouseUpStateMachine,
        type: "mouseup",
      });
      unregisterEvent({
        forMap: true,
        listener: onMouseUp,
        type: "mouseup",
      });
      unregisterEvent({
        forMap: true,
        listener: onDeckGlMouseOutStateMachine,
        type: "mouseout",
      });
      unregisterEvent({
        listener: onMouseEnter,
        type: "mouseenter",
      });
      unregisterEvent({
        listener: onMouseEnterStateMachine,
        type: "mouseenter",
      });
      unregisterEvent({
        listener: onMouseLeave,
        type: "mouseleave",
      });
      unregisterEvent({
        listener: onMouseLeaveStateMachine,
        type: "mouseleave",
      });

      // Touch events
      unregisterEvent({
        listener: onTouchStartStateMachine,
        type: "touchstart",
      });
      unregisterEvent({
        forMap: true,
        listener: onTouchMove,
        type: "touchmove",
      });
      unregisterEvent({
        forMap: true,
        listener: onTouchMoveStateMachine,
        type: "touchmove",
      });
      unregisterEvent({
        forMap: true,
        listener: onTouchEndStateMachine,
        type: "touchend",
      });
      unregisterEvent({
        forMap: true,
        listener: onTouchEnd,
        type: "touchend",
      });
    };
  }, [
    map,
    deckMap,
    layerIds,
    onClick,
    onDrag,
    onDragStart,
    onDragEnd,
    onHover,
    onHoverEnd,
    priority,
    options.registerOnMap,
    options.enableDeckglHover,
  ]);
};

export default useMapEventHandler;
